import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from '@/components/ui';
import {
  VendorInvoice,
  VendorInvoiceAttachment,
  VendorInvoiceCreateData,
  VendorInvoiceUpdateData,
  VendorInvoiceStatus,
} from '../../../types/VendorInvoice';
import { parseCalendarDate } from '../../../utils/dateUtils';

interface VendorInvoiceModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (invoiceData: VendorInvoiceCreateData | VendorInvoiceUpdateData, file?: File) => Promise<void>;
  invoice?: VendorInvoice;
  projectId: number;
}

const VendorInvoiceModal: React.FC<VendorInvoiceModalProps> = ({ visible, onClose, onSave, invoice }) => {
  const [formData, setFormData] = useState<VendorInvoiceCreateData | VendorInvoiceUpdateData>({
    invoiceDate: '',
    invoiceValue: '',
    invoiceNumber: '',
    status: 'New',
    comments: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [existingAttachment, setExistingAttachment] = useState<VendorInvoiceAttachment | null>(null);

  const isEditing = !!invoice;

  useEffect(() => {
    if (visible && invoice) {
      let invoiceDate = '';
      // The API returns invoiceDate as a UTC-midnight ISO string for what is really just a
      // calendar date. Reading it back with local getters (as this used to for both branches)
      // shows the *previous* day in any timezone behind UTC — parseCalendarDate anchors it at
      // local midnight for the same calendar day instead, so the date input pre-fills correctly.
      const parsedDate = parseCalendarDate(invoice.invoiceDate);
      if (parsedDate) {
        const year = parsedDate.getFullYear();
        const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
        const day = String(parsedDate.getDate()).padStart(2, '0');
        invoiceDate = `${year}-${month}-${day}`;
      }

      setFormData({
        invoiceDate,
        invoiceValue: invoice.invoiceValue.toString(),
        invoiceNumber: invoice.invoiceNumber || '',
        status: invoice.status as VendorInvoiceStatus,
        comments: invoice.comments || '',
      });

      if (invoice.attachments && invoice.attachments.length > 0) {
        setExistingAttachment(invoice.attachments[0]);
      } else {
        setExistingAttachment(null);
      }

      setSelectedFile(null);
    } else if (visible && !invoice) {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const localDateString = `${year}-${month}-${day}`;

      setFormData({
        invoiceDate: localDateString,
        invoiceValue: '',
        invoiceNumber: '',
        status: 'New',
        comments: '',
      });
      setSelectedFile(null);
      setExistingAttachment(null);
    }
  }, [visible, invoice]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) setSelectedFile(e.target.files[0]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const createData = formData as VendorInvoiceCreateData;
      if (!createData.invoiceDate) throw new Error('Invoice Date is required');
      if (!createData.invoiceValue) throw new Error('Invoice Value is required');
      if (!createData.status) throw new Error('Status is required');

      const invoicePayload: VendorInvoiceCreateData | VendorInvoiceUpdateData = {
        ...formData,
        invoiceValue: parseFloat(createData.invoiceValue.toString()),
      };

      await onSave(invoicePayload, selectedFile || undefined);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      invoiceDate: '',
      invoiceValue: '',
      invoiceNumber: '',
      status: 'New',
      comments: '',
    });
    setError(null);
    setSelectedFile(null);
    setExistingAttachment(null);
    onClose();
  };

  return (
    <Modal show={visible} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? 'Edit Vendor Invoice' : 'Add Vendor Invoice'}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Invoice Date *</Form.Label>
                <Form.Control
                  type="date"
                  name="invoiceDate"
                  value={String(formData.invoiceDate || '')}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Invoice Value *</Form.Label>
                <Form.Control
                  type="number"
                  name="invoiceValue"
                  value={String(formData.invoiceValue || '')}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Invoice Number (Internal)</Form.Label>
                <Form.Control
                  type="text"
                  name="invoiceNumber"
                  value={String(formData.invoiceNumber || '')}
                  onChange={handleChange}
                  placeholder="Optional internal reference"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="tw-mb-3">
                <Form.Label>Status *</Form.Label>
                <Form.Select
                  name="status"
                  value={String(formData.status || 'New')}
                  onChange={handleChange}
                  required
                >
                  <option value="New">New</option>
                  <option value="Finalized">Finalized</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="tw-mb-3">
            <Form.Label>Comments</Form.Label>
            <Form.Control
              as="textarea"
              name="comments"
              value={String(formData.comments || '')}
              onChange={handleChange}
              rows={3}
              placeholder="Optional comments about this invoice"
            />
          </Form.Group>

          <Form.Group className="tw-mb-3">
            <Form.Label>Vendor Invoice PDF Attachment</Form.Label>
            <Form.Control type="file" accept=".pdf" onChange={handleFileChange} />
            <Form.Text className="tw-text-content-muted">Only PDF files are allowed. Maximum size: 10MB</Form.Text>

            {isEditing && existingAttachment && (
              <div className="tw-mt-2">
                <small className="tw-text-info">
                  <i className="bi bi-file-pdf tw-mr-1"></i>
                  Current attachment: {existingAttachment.attachmentName}
                </small>
                <div className="tw-mt-1">
                  <small className="tw-text-content-muted">Upload a new file to replace the existing attachment</small>
                </div>
              </div>
            )}

            {selectedFile && (
              <div className="tw-mt-2">
                <small className="tw-text-success">
                  <i className="bi bi-check-circle tw-mr-1"></i>
                  New file selected: {selectedFile.name}
                </small>
              </div>
            )}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? 'Saving...' : isEditing ? 'Update Vendor Invoice' : 'Add Vendor Invoice'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default VendorInvoiceModal;

